/**
 * A scanner-safe sign-in challenge (#2668), not a signup/session callback.
 * Reading only scrubs and returns memory-owned input. The host must obtain an
 * explicit Continue action before calling the canonical AuthClient exchange.
 */
export interface SignInChallenge {
  tokenHash: string
  verificationType: 'signup' | 'magiclink'
}

export interface SignInChallengeReadResult {
  challenge: SignInChallenge | null
  error: { code: 'invalid_signin_challenge'; description: string } | null
}

const TOKEN_HASH = /^[A-Za-z0-9_-]{40,128}$/
const invalid = (): SignInChallengeReadResult => ({
  challenge: null,
  error: { code: 'invalid_signin_challenge', description: 'That sign-in link is not valid.' },
})

/** Shared runtime validation; untyped hosts cannot turn recovery into login. */
export function isSignInChallenge(value: unknown): value is SignInChallenge {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const keys = Object.keys(value)
  const challenge = value as Partial<SignInChallenge>
  return keys.length === 2 && keys.includes('tokenHash') && keys.includes('verificationType')
    && typeof challenge.tokenHash === 'string' && TOKEN_HASH.test(challenge.tokenHash)
    && (challenge.verificationType === 'signup' || challenge.verificationType === 'magiclink')
}

export function readSignInChallengeFromHash(
  loc: Pick<Location, 'hash' | 'pathname' | 'search'> | undefined =
    typeof window !== 'undefined' ? window.location : undefined,
  hist: Pick<History, 'replaceState'> | undefined =
    typeof window !== 'undefined' ? window.history : undefined,
): SignInChallengeReadResult {
  const empty: SignInChallengeReadResult = { challenge: null, error: null }
  if (!loc?.hash || loc.hash.length < 2) return empty
  const params = new URLSearchParams(loc.hash.slice(1))
  // A duplicate type may hide the recognized purpose behind an unrelated one.
  // Scrub it too, then refuse rather than treating the first value as authority.
  if (!params.getAll('type').includes('participant_signin')) return empty

  try {
    if (typeof hist?.replaceState !== 'function') return invalid()
    hist.replaceState(null, '', `${loc.pathname}${loc.search}`)
  } catch {
    // Never hand the bearer to application code while it remains in history.
    return invalid()
  }

  const keys = [...params.keys()]
  if (keys.length !== 3 || params.getAll('type').length !== 1
    || params.getAll('verification_type').length !== 1 || params.getAll('token_hash').length !== 1
    || keys.some((key) => !['type', 'verification_type', 'token_hash'].includes(key))) return invalid()
  const challenge = {
    tokenHash: params.get('token_hash'),
    verificationType: params.get('verification_type'),
  }
  if (!isSignInChallenge(challenge)) return invalid()
  return { challenge: Object.freeze(challenge), error: null }
}
